// src/components/Ship2Air.js
import React, { useState } from 'react';
import { Formik, Field, FieldArray, Form } from 'formik';
import { Row, Col, Button, FormGroup, Label, Input, InputGroup, InputGroupText, Tooltip } from 'reactstrap';
import { useDispatch, useSelector } from 'react-redux';
import Select from 'react-select';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCopy, faPlus, faMinus } from '@fortawesome/free-solid-svg-icons';
import * as Yup from 'yup';
import usePlacesAutocomplete, { getGeocode } from "use-places-autocomplete";
import CommodityCascader from 'utils/CommodityCascader';
import { setActiveFlow, setCurrentStep, setFormData } from 'store/slices/flowSlice';
import { useTranslation } from "react-i18next";
import AutoFillAddress from 'utils/AutoFillAddress';



const initialItem = {
  pieces: '',
  packageType: '',
  commodity: '',
  weightPerPiece: '',
  totalWeight: '',
  lengthPerPiece: '',
  widthPerPiece: '',
  heightPerPiece: '',
  volumeTotal: '',
  chargeableWeight: '',
  cargoDescription: '',
  hsCode: '',
  dangerousGoods: false,
  stackable: false,
};

const initialReference = {
  referenceType: '',
  referenceNo: '',
};

const initialAddress = {
  addressLine: '',
  streetNo: '',
  city: '',
  state: '',
  country: '',
};

const calculateTotalWeight = (pieces, weightPerPiece) => {
  let totalWeight = pieces && weightPerPiece ? pieces * weightPerPiece : 0;
  return totalWeight.toFixed(2);
};

const calculateVolume = (length, width, height, numberOfPieces) => {
  let volume = length && width && height * numberOfPieces ? (length * width * height * numberOfPieces) : 0;
  volume = volume * 1e-6;
  volume = volume < 0.01 ? 0.01 : volume;
  return volume.toFixed(2); // Round off to 2 decimal places
};

const calculateChargeableWeight = (length, width, height, numberOfPieces, totalWeight) => {
  let calculatedWeight = ((length * width * height) / 6000) * (numberOfPieces || 1);
  let chargeableWeight = calculatedWeight > totalWeight ? calculatedWeight : totalWeight;
  return !!chargeableWeight ? Number(chargeableWeight).toFixed(2) : 0;
};



const Ship2Air = () => {
  const [cargoReadyDate, setCargoReadyDate] = useState(new Date());
  const [lcHandlingTooltipOpen, setLcHandlingTooltipOpen] = useState(false);
  const [securedShipmentTooltipOpen, setSecuredShipmentTooltipOpen] = useState(false);


  // Implement Google Places Autocomplete
  const {
    ready,
    value,
    suggestions: { status, data },
    setValue,
    clearSuggestions,
  } = usePlacesAutocomplete({
    requestOptions: {
      types: ['address'],
    },
    debounce: 300,
  });

  const dispatch = useDispatch();
  const { t } = useTranslation();
  const pickupRequired = useSelector((state) => state.flow.pickupRequired);

  const recipientOptions = [
    { value: 'business', label: t("Business/Company") },
    { value: 'private', label: t("Private Home") },
  ];

  const packageTypes = [
    { value: 'europallet', label: t('Europallet') },
    { value: 'carton', label: t('Carton') },
    { value: 'one-way-pallet', label: t('One-way pallet') },
    { value: 'crate', label: t('Crate') },
    { value: 'bundle', label: t('Bundle') },
  ];
  
  const referenceOptions = [
    { value: 'shippers-reference', label: t('Shipper\'s Reference No.') },
    { value: 'consignees-reference', label: t('Consignee\'s Reference No.') },
  ];


  // Define validation schema
  const validationSchema = Yup.object().shape({
    cargoReadyDate: Yup.date().optional(),
    incoterm: Yup.string().optional(),
    incotermLocation: Yup.string().optional(),
    role: Yup.string().optional(),
    items: Yup.array().of(
      Yup.object().shape({
        pieces: Yup.number().required(t('Pieces are required')).positive().integer(),
        packageType: Yup.object().required(t('Package type is required')).nullable(),
        commodity: Yup.string().required(t('Commodity is required')).nullable(),
        weightPerPiece: Yup.number().required(t('Weight per piece is required')).positive(),
        lengthPerPiece: Yup.number().required(t('Length per piece is required')).positive(),
        widthPerPiece: Yup.number().required(t('Width per piece is required')).positive(),
        heightPerPiece: Yup.number().required(t('Height per piece is required')).positive(),
        cargoDescription: Yup.string().optional(),
        hsCode: Yup.string().optional(),
      })
    ),
    references: Yup.array().of(
      Yup.object().shape({
        referenceType: Yup.object().optional(),
        referenceNo: Yup.string().optional(),
      })
    ),
    collectionAddress: Yup.object().shape({
      addressLine: pickupRequired ? Yup.string().required(t('Address Line is required')) : Yup.string().optional(),
      streetNo: pickupRequired ? Yup.string().required(t('Street No. is required')) : Yup.string().optional(),
      city: pickupRequired ? Yup.string().required(t('City is required')) : Yup.string().optional(),
      state: pickupRequired ? Yup.string().required(t('State is required')) : Yup.string().optional(),
      country: pickupRequired ? Yup.string().required(t('Country is required')) : Yup.string().optional(),
    }),
    note: Yup.string().optional(),
  });

  const handleSubmit = (values) => {
    dispatch(setActiveFlow('air'));
    dispatch(setCurrentStep('2'));
    dispatch(setFormData(values));
  };

  const handleSelectAddress = async (address, setFieldValue) => {
    try {
      const results = await getGeocode({ address });
      const addressComponents = results[0].address_components;

      const addressLine = `${addressComponents.find(c => c.types.includes('route'))?.long_name || ''}`;
      const streetNo = `${addressComponents.find(c => c.types.includes('street_number'))?.long_name || ''}`;
      const city = `${addressComponents.find(c => c.types.includes('locality'))?.long_name || ''}`;
      const state = `${addressComponents.find(c => c.types.includes('administrative_area_level_1'))?.long_name || ''}`;
      const country = `${addressComponents.find(c => c.types.includes('country'))?.long_name || ''}`;

      setFieldValue('collectionAddress.addressLine', addressLine);
      setFieldValue('collectionAddress.streetNo', streetNo);
      setFieldValue('collectionAddress.city', city);
      setFieldValue('collectionAddress.state', state);
      setFieldValue('collectionAddress.country', country);

      setValue(address, false);
      clearSuggestions();
    } catch (error) {
      console.error("Error: ", error);
    }
  };

  return (
    <div className="e2g-shipping-step2-form">
      <div className="e2g-shipping-step2-container container">
        <div className="e2g-shipping-step2-form-wrap">
          <Formik
            initialValues={{
              items: [initialItem],
              references: [initialReference],
              cargoReadyDate: new Date(),
              lcHandling: false,
              securedShipment: false,
              incoterm: '',
              incotermLocation: '',
              role: '',
              collectionAddress: initialAddress,
              note: '',
            }}
            validationSchema={validationSchema}
            onSubmit={handleSubmit}
          >
            {({ values, setFieldValue, errors, touched }) => (
              <Form className='e2g-form-step e2g-air-step form-step'>
                <h4 className="e2g-step2-head mb-2" style={{ fontSize: '32px' }}>
                  {t("What are the cargo details?")}
                </h4>
                <AutoFillAddress handleSelectAddress={handleSelectAddress} />
                {/* Items FieldArray */}
                <FieldArray
                  name="items"
                  render={arrayHelpers => (
                    <div className="e2g-shipping-field-array-wrap">
                      {values.items && values.items.length > 0 ? (
                        values.items.map((item, index) => (
                          <div key={index} className="e2g-shipping-field-item">
                            <Row className="e2g-shipping-field-array mb-3">
                              <Col sm="4">
                                <FormGroup className="e2g-cargo-form-group">
                                  <Label className="e2g-cargo-form-label" for={`items.${index}.pieces`}>{t("Pieces")}</Label>
                                  <Field
                                    className="bg-light e2g-custom-height rounded-0 border-0 e2g-custom-fonts"
                                    name={`items.${index}.pieces`}
                                    type="number"
                                    min="0"
                                    as={Input}
                                    onChange={(e) => {
                                      const pieces = e.target.value;
                                      setFieldValue(`items.${index}.pieces`, pieces);
                                      const totalWeight = calculateTotalWeight(pieces, values.items[index].weightPerPiece);
                                      setFieldValue(`items.${index}.totalWeight`, totalWeight);
                                      const volumeTotal = calculateVolume(values.items[index].lengthPerPiece, values.items[index].widthPerPiece, values.items[index].heightPerPiece, pieces);
                                      setFieldValue(`items.${index}.volumeTotal`, volumeTotal);
                                      const chargeableWeight = calculateChargeableWeight(values.items[index].lengthPerPiece, values.items[index].widthPerPiece, values.items[index].heightPerPiece, pieces, totalWeight);
                                      setFieldValue(`items.${index}.chargeableWeight`, chargeableWeight);
                                    }}
                                  />
                                  {errors.items && errors.items[index] && errors.items[index].pieces && touched.items && touched.items[index] && touched.items[index].pieces ? (
                                    <div className="e2g-error-text text-danger">{errors.items[index].pieces}</div>
                                  ) : null}
                                </FormGroup>
                              </Col>
                              <Col sm="4">
                                <FormGroup className="e2g-cargo-form-group">
                                  <Label className="e2g-cargo-form-label" for={`items.${index}.packageType`}>{t("Package Type")}</Label>
                                  <Select
                                    className='bg-light e2g-custom-height e2g-custom-fonts'
                                    id={`items.${index}.packageType`}
                                    options={packageTypes}
                                    onChange={option => setFieldValue(`items.${index}.packageType`, option)}
                                  />
                                  {errors.items && errors.items[index] && errors.items[index].packageType && touched.items && touched.items[index] && touched.items[index].packageType ? (
                                    <div className="e2g-error-text text-danger">{errors.items[index].packageType}</div>
                                  ) : null}
                                </FormGroup>
                              </Col>

                              {/* Commodity Field */}
                              <Col sm="4">
                                <FormGroup className="e2g-cargo-form-group">
                                  <Label className="e2g-cargo-form-label" for={`items.${index}.commodity`}>{t("Commodity")}</Label>
                                  <CommodityCascader 
                                    searchable={false}
                                    size="lg"
                                    onChange={option => setFieldValue(`items.${index}.commodity`, option)}
                                    placeholder={t("Select commodity")}
                                  />
                                  {errors.items && errors.items[index] && errors.items[index].commodity && touched.items && touched.items[index] && touched.items[index].commodity ? (
                                    <div className="e2g-error-text text-danger">{errors.items[index].commodity}</div>
                                  ) : null}
                                </FormGroup>
                              </Col>

                              <Col sm="2">
                                <FormGroup className="e2g-cargo-form-group">
                                  <Label className="e2g-cargo-form-label" for={`items.${index}.weightPerPiece`}>{t("Weight/Piece")}</Label>
                                  <InputGroup>
                                    <Field
                                      className='bg-light e2g-custom-height rounded-0 border-0'
                                      name={`items.${index}.weightPerPiece`}
                                      type="number"
                                      min="0"
                                      step="0.01"
                                      as={Input}
                                      onChange={(e) => {
                                        const weightPerPiece = e.target.value;
                                        setFieldValue(`items.${index}.weightPerPiece`, weightPerPiece);
                                        const totalWeight = calculateTotalWeight(values.items[index].pieces, weightPerPiece);
                                        setFieldValue(`items.${index}.totalWeight`, totalWeight);
                                        const chargeableWeight = calculateChargeableWeight(values.items[index].lengthPerPiece, values.items[index].widthPerPiece, values.items[index].heightPerPiece, values.items[index].pieces, totalWeight);
                                        setFieldValue(`items.${index}.chargeableWeight`, chargeableWeight);
                                      }}
                                    />
                                    <InputGroupText className='rounded-0 border-0 bg-light e2g-custom-height e2g-custom-fonts'>{t("kg")}</InputGroupText>
                                  </InputGroup>
                                  {errors.items && errors.items[index] && errors.items[index].weightPerPiece && touched.items && touched.items[index] && touched.items[index].weightPerPiece ? (
                                    <div className="e2g-error-text text-danger">{errors.items[index].weightPerPiece}</div>
                                  ) : null}
                                </FormGroup>
                              </Col>
                              <Col sm="2">
                                <FormGroup className="e2g-cargo-form-group">
                                  <Label className="e2g-cargo-form-label" for={`items.${index}.totalWeight`}>{t("Weight")}</Label>
                                  <InputGroup>
                                    <Field
                                      className='bg-light e2g-custom-height rounded-0 border-0'
                                      name={`items.${index}.totalWeight`}
                                      type="number"
                                      min="0"
                                      as={Input}
                                      readOnly
                                    />
                                    <InputGroupText className='rounded-0 border-0 bg-light e2g-custom-height e2g-custom-fonts'>{t("kg")}</InputGroupText>
                                  </InputGroup>
                                </FormGroup>
                              </Col>
                              <Col sm="2">
                                <FormGroup className="e2g-cargo-form-group">
                                  <Label className="e2g-cargo-form-label" for={`items.${index}.lengthPerPiece`}>{t("Length/Piece")}</Label>
                                  <InputGroup>
                                    <Field
                                      className='bg-light e2g-custom-height rounded-0 border-0'
                                      name={`items.${index}.lengthPerPiece`}
                                      type="number"
                                      min="0"
                                      as={Input}
                                      onChange={(e) => {
                                        const lengthPerPiece = e.target.value;
                                        setFieldValue(`items.${index}.lengthPerPiece`, lengthPerPiece);
                                        const volumeTotal = calculateVolume(lengthPerPiece, values.items[index].widthPerPiece, values.items[index].heightPerPiece, values.items[index].pieces);
                                        setFieldValue(`items.${index}.volumeTotal`, volumeTotal);
                                        const chargeableWeight = calculateChargeableWeight(lengthPerPiece, values.items[index].widthPerPiece, values.items[index].heightPerPiece, values.items[index].pieces, values.items[index].totalWeight);
                                        setFieldValue(`items.${index}.chargeableWeight`, chargeableWeight);
                                      }}
                                    />
                                    <InputGroupText className='rounded-0 border-0 bg-light e2g-custom-height e2g-custom-fonts'>{t("cm")}</InputGroupText>
                                  </InputGroup>
                                  {errors.items && errors.items[index] && errors.items[index].lengthPerPiece && touched.items && touched.items[index] && touched.items[index].lengthPerPiece ? (
                                    <div className="e2g-error-text text-danger">{errors.items[index].lengthPerPiece}</div>
                                  ) : null}
                                </FormGroup>
                              </Col>
                              <Col sm="2">
                                <FormGroup className="e2g-cargo-form-group">
                                  <Label className="e2g-cargo-form-label" for={`items.${index}.widthPerPiece`}>{t("Width/Piece")}</Label>
                                  <InputGroup>
                                    <Field
                                      className='bg-light e2g-custom-height rounded-0 border-0'
                                      name={`items.${index}.widthPerPiece`}
                                      type="number"
                                      min="0"
                                      as={Input}
                                      onChange={(e) => {
                                        const widthPerPiece = e.target.value;
                                        setFieldValue(`items.${index}.widthPerPiece`, widthPerPiece);
                                        const volumeTotal = calculateVolume(values.items[index].lengthPerPiece, widthPerPiece, values.items[index].heightPerPiece, values.items[index].pieces);
                                        setFieldValue(`items.${index}.volumeTotal`, volumeTotal);
                                        const chargeableWeight = calculateChargeableWeight(values.items[index].lengthPerPiece, widthPerPiece, values.items[index].heightPerPiece, values.items[index].pieces, values.items[index].totalWeight);
                                        setFieldValue(`items.${index}.chargeableWeight`, chargeableWeight);
                                      }}
                                    />
                                    <InputGroupText className='rounded-0 border-0 bg-light e2g-custom-height e2g-custom-fonts'>{t("cm")}</InputGroupText>
                                  </InputGroup>
                                  {errors.items && errors.items[index] && errors.items[index].widthPerPiece && touched.items && touched.items[index] && touched.items[index].widthPerPiece ? (
                                    <div className="e2g-error-text text-danger">{errors.items[index].widthPerPiece}</div>
                                  ) : null}
                                </FormGroup>
                              </Col>
                              <Col sm="2">
                                <FormGroup className="e2g-cargo-form-group">
                                  <Label className="e2g-cargo-form-label" for={`items.${index}.heightPerPiece`}>{t("Height/Piece")}</Label>
                                  <InputGroup>
                                    <Field
                                      className='bg-light e2g-custom-height rounded-0 border-0'
                                      name={`items.${index}.heightPerPiece`}
                                      type="number"
                                      min="0"
                                      as={Input}
                                      onChange={(e) => {
                                        const heightPerPiece = e.target.value;
                                        setFieldValue(`items.${index}.heightPerPiece`, heightPerPiece);
                                        const volumeTotal = calculateVolume(values.items[index].lengthPerPiece, values.items[index].widthPerPiece, heightPerPiece, values.items[index].pieces);
                                        setFieldValue(`items.${index}.volumeTotal`, volumeTotal);
                                        const chargeableWeight = calculateChargeableWeight(values.items[index].lengthPerPiece, values.items[index].widthPerPiece, heightPerPiece, values.items[index].pieces, values.items[index].totalWeight);
                                        setFieldValue(`items.${index}.chargeableWeight`, chargeableWeight);
                                      }}
                                    />
                                    <InputGroupText className='rounded-0 border-0 bg-light e2g-custom-height e2g-custom-fonts'>{t("cm")}</InputGroupText>
                                  </InputGroup>
                                  {errors.items && errors.items[index] && errors.items[index].heightPerPiece && touched.items && touched.items[index] && touched.items[index].heightPerPiece ? (
                                    <div className="e2g-error-text text-danger">{errors.items[index].heightPerPiece}</div>
                                  ) : null}
                                </FormGroup>
                              </Col>
                              <Col sm="2">
                                <FormGroup className="e2g-cargo-form-group">
                                  <Label className="e2g-cargo-form-label" for={`items.${index}.volumeTotal`}>{t("Volume (total)")}</Label>
                                  <InputGroup>
                                    <Field
                                      className='bg-light e2g-custom-height rounded-0 border-0'
                                      name={`items.${index}.volumeTotal`}
                                      type="number"
                                      min="0"
                                      as={Input}
                                      readOnly
                                    />
                                    <InputGroupText className='rounded-0 border-0 bg-light e2g-custom-height e2g-custom-fonts'>{t("m³")}</InputGroupText>
                                  </InputGroup>
                                </FormGroup>
                              </Col>
                              <Col sm="6">
                                <FormGroup className="e2g-cargo-form-group">
                                  <Label className="e2g-cargo-form-label" for={`items.${index}.cargoDescription`}>{t("Cargo Description")}</Label>
                                  <Field
                                    className='bg-light e2g-custom-height rounded-0 border-0'
                                    name={`items.${index}.cargoDescription`}
                                    type="text"
                                    as={Input}
                                  />
                                  {errors.items && errors.items[index] && errors.items[index].cargoDescription && touched.items && touched.items[index] && touched.items[index].cargoDescription ? (
                                    <div className="e2g-error-text text-danger">{errors.items[index].cargoDescription}</div>
                                  ) : null}
                                </FormGroup>
                              </Col>
                              <Col sm="2">
                                <FormGroup className="e2g-cargo-form-group">
                                  <Label className="e2g-cargo-form-label" for={`items.${index}.hsCode`}>{t("HS Code")}</Label>
                                  <Field
                                    className='bg-light e2g-custom-height rounded-0 border-0'
                                    name={`items.${index}.hsCode`}
                                    type="text"
                                    as={Input}
                                  />
                                  {errors.items && errors.items[index] && errors.items[index].hsCode && touched.items && touched.items[index] && touched.items[index].hsCode ? (
                                    <div className="e2g-error-text text-danger">{errors.items[index].hsCode}</div>
                                  ) : null}
                                </FormGroup>
                              </Col>
                              <div className="e2g-push-icons d-flex align-items-end flex-row-reverse">
                                <div className="d-flex align-items-end gap-2 mb-3">
                                  <Button color="secondary" className='p-1 d-flex' onClick={() => arrayHelpers.insert(index, item)}>
                                    <FontAwesomeIcon icon={faCopy} />
                                  </Button>
                                  <Button color="primary" className='p-1 d-flex' onClick={() => arrayHelpers.push(initialItem)}>
                                    <FontAwesomeIcon icon={faPlus} />
                                  </Button>
                                  {values.items.length > 1 && (
                                    <Button color="danger" className='p-1 d-flex' onClick={() => arrayHelpers.remove(index)}>
                                      <FontAwesomeIcon icon={faMinus} />
                                    </Button>
                                  )}
                                </div>
                              </div>
                            </Row>
                          </div>
                        ))
                      ) : (
                        <Button type="button" onClick={() => arrayHelpers.push(initialItem)}>
                          {t("Add an item")}
                        </Button>
                      )}
                    </div>
                  )}
                />

                <div style={{ background: '#cae7ea', padding: '.85rem .5rem' }} className="d-flex justify-content-between align-items-center gap-2 e2g-total-value">
                  <h6 className='fw-bold m-0'>{t("Total")}</h6>
                  <div className='d-flex gap-4 e2g-total-value-cont'>
                    <p className='m-0'>{t("Pieces")}: {values.items.reduce((sum, item) => sum + Number(item.pieces || 0), 0)}</p>
                    <p className='m-0'>{t("Weight")}: {values.items.reduce((sum, item) => sum + Number(item.totalWeight || 0), 0)} {t("kg")}</p>
                    <p className='m-0'>{t("Chargeable Weight")}: {values.items.reduce((sum, item) => sum + Number(item.chargeableWeight || 0), 0)} {t("kg")}</p>
                    <p className='m-0'>{("Volume")}: {values.items.reduce((sum, item) => sum + Number(item.volumeTotal || 0), 0)} {t("m³")}</p>
                  </div>
                </div>

                {pickupRequired && (<>
                  <Row className="mb-3">
                  <Col sm="12">
                    <FormGroup className="e2g-cargo-form-group">
                      <Label className="e2g-cargo-form-label" for="collectionAddress"><strong>{t("Collection Address")}</strong></Label>
                      <Input
                        id="collectionAddress"
                        value={value}
                        onChange={(e) => setValue(e.target.value)}
                        disabled={!ready}
                        placeholder={t("Type to search...")}
                        className="bg-light e2g-custom-height rounded-0 border-0"
                      />
                      {status === "OK" && (
                        <ul className="autocomplete-dropdown">
                          {data.map(({ place_id, description }) => (
                            <li
                              key={place_id}
                              onClick={() => handleSelectAddress(description, setFieldValue)}
                            >
                              {description}
                            </li>
                          ))}
                        </ul>
                      )}
                      {errors.collectionAddress && touched.collectionAddress ? (
                        <div className="e2g-error-text text-danger">
                          {Object.values(errors.collectionAddress).join(', ')}
                        </div>
                      ) : null}
                    </FormGroup>
                  </Col>

                  {/* Address Line Field */}

                  <Col sm="12">
                    <FormGroup className="e2g-cargo-form-group">
                      <Label className="e2g-cargo-form-label" for="addressLine">{t("Address Line")}</Label>
                      <Field
                        name="collectionAddress.addressLine"
                        id="addressLine"
                        as={Input}
                        className="bg-light e2g-custom-height rounded-0 border-0"
                      />
                      {errors.collectionAddress?.addressLine && touched.collectionAddress?.addressLine ? (
                        <div className="e2g-error-text text-danger">{errors.collectionAddress.addressLine}</div>
                      ) : null}
                    </FormGroup>
                  </Col>

                  {/* Street Number Field */}
                  <Col sm="12">
                    <FormGroup className="e2g-cargo-form-group">
                      <Label className="e2g-cargo-form-label" for="streetNo">{t("Street Number")}</Label>
                      <Field
                        name="collectionAddress.streetNo"
                        id="streetNo"
                        as={Input}
                        className="bg-light e2g-custom-height rounded-0 border-0"
                      />
                      {errors.collectionAddress?.streetNo && touched.collectionAddress?.streetNo ? (
                        <div className="e2g-error-text text-danger">{errors.collectionAddress.streetNo}</div>
                      ) : null}
                    </FormGroup>
                  </Col>


                  {/* City Field */}

                  <Col sm="3">
                    <FormGroup className="e2g-cargo-form-group">
                      <Label className="e2g-cargo-form-label" for="city">{t("City")}</Label>
                      <Field
                        name="collectionAddress.city"
                        id="city"
                        as={Input}
                        className="bg-light e2g-custom-height rounded-0 border-0"
                      />
                      {errors.collectionAddress?.city && touched.collectionAddress?.city ? (
                        <div className="e2g-error-text text-danger">{errors.collectionAddress.city}</div>
                      ) : null}
                    </FormGroup>
                  </Col>

                  {/* State Field */}
                  <Col sm="3">
                    <FormGroup className="e2g-cargo-form-group">
                      <Label className="e2g-cargo-form-label" for="state">{t("State")}</Label>
                      <Field
                        name="collectionAddress.state"
                        id="state"
                        as={Input}
                        className="bg-light e2g-custom-height rounded-0 border-0"
                      />
                      {errors.collectionAddress?.state && touched.collectionAddress?.state ? (
                        <div className="e2g-error-text text-danger">{errors.collectionAddress.state}</div>
                      ) : null}
                    </FormGroup>
                  </Col>


                  {/* Country and ZipCode Fields */}

                  <Col sm="3">
                    <FormGroup className="e2g-cargo-form-group">
                      <Label className="e2g-cargo-form-label" for="country">{t("Country")}</Label>
                      <Field
                        name="collectionAddress.country"
                        id="country"
                        as={Input}
                        className="bg-light e2g-custom-height rounded-0 border-0"
                      />
                      {errors.collectionAddress?.country && touched.collectionAddress?.country ? (
                        <div className="e2g-error-text text-danger">{errors.collectionAddress.country}</div>
                      ) : null}
                    </FormGroup>
                  </Col>

                  <Col sm="3">
                    <FormGroup className="e2g-cargo-form-group">
                      <Label className="e2g-cargo-form-label" for="zipCode">{t("Zip Code")}</Label>
                      <Field
                        name="collectionAddress.zipCode"
                        id="zipCode"
                        as={Input}
                        className="bg-light e2g-custom-height rounded-0 border-0"
                      />
                      {errors.collectionAddress?.zipCode && touched.collectionAddress?.zipCode ? (
                        <div className="e2g-error-text text-danger">{errors.collectionAddress.zipCode}</div>
                      ) : null}
                    </FormGroup>
                  </Col>
                </Row>
                </>)}
                {/* Collection Address Field with Google Autocomplete */}


                <div className='e2g-pickup-recipent d-flex w-75 gap-3'>
                  <FormGroup className="mt-0 d-flex mb-0 col-6 align-items-start gap-3 flex-column">
                    <Label className='col-5 m-0 d-flex align-items-center' for="cargoReadyDate">{t("Cargo Ready Date")}</Label>
                    <DatePicker
                      id="cargoReadyDate"
                      calendarClassName="e2g-calendar"
                      selected={cargoReadyDate}
                      onChange={date => {setCargoReadyDate(date); setFieldValue('cargoReadyDate', date)}}
                      className="e2g-ready-date bg-light e2g-custom-height rounded-0 border-0 e2g-custom-fonts form-control w-100"
                    />
                    {errors.cargoReadyDate && touched.cargoReadyDate ? (
                      <div className="e2g-error-text text-danger">{errors.cargoReadyDate}</div>
                    ) : null}
                  </FormGroup>
                  <FormGroup className='mt-0 d-flex mb-0 col-6 align-items-start gap-3 flex-column'>
                    <Label className="e2g-cargo-form-label col-5 m-0 d-flex align-items-center" for="shipmentRecipient" >{t("Shipment Recipient")}</Label>
                    <Select
                      className="bg-light e2g-custom-height rounded-0 border-0 e2g-custom-fonts form-control p-0 w-100"
                      id="shipmentRecipient"
                      options={recipientOptions}
                      onChange={option => setFieldValue('shipmentRecipient', option)}
                    />
                    {errors.shipmentRecipient && touched.shipmentRecipient ? (
                      <div className="e2g-error-text text-danger">{errors.shipmentRecipient}</div>
                    ) : null}
                  </FormGroup>
                </div>



                {/* Note Field */}
                <Row className="mb-0">
                  <Col sm="12">
                    <FormGroup className="e2g-cargo-form-group">
                      <Label className="e2g-cargo-form-label" for="note">{t("Note")}</Label>
                      <Field
                        className='e2g-note bg-light e2g-custom-height rounded-0 border-0'
                        name="note"
                        type="textarea"
                        as={Input}
                      />
                      {errors.note && touched.note ? (
                        <div className="e2g-error-text text-danger">{errors.note}</div>
                      ) : null}
                    </FormGroup>
                  </Col>
                </Row>

                {/* Handling Section */}
                <h4 className='fw-bold'>{t("Handling")}</h4>
                <div className='e2g-shipping-handling'>
                  <FormGroup check className='col-6'>
                    <Label check for="lcHandling">
                      <Field
                        type="checkbox"
                        name="lcHandling"
                        as={Input}
                        id="lcHandling"
                      />{' '}
                      {t('L/C Handling')}
                    </Label>
                    <Tooltip
                      placement="right"
                      isOpen={lcHandlingTooltipOpen}
                      target="lcHandling"
                      toggle={() => setLcHandlingTooltipOpen(!lcHandlingTooltipOpen)}
                    >
                      {t("A Letter of Credit (L/C) is an advance instruction by a bank to an overseas bank to guarantee payment to the seller once certain delivery conditions have been met. This usually comes with special requirements for the transport.")}
                    </Tooltip>
                  </FormGroup>
                  <FormGroup check className='col-6'>
                    <Label check for="securedShipment">
                      <Field
                        type="checkbox"
                        name="securedShipment"
                        as={Input}
                        id="securedShipment"
                      />{' '}
                      {t("Secured Shipment")}
                    </Label>
                    <Tooltip
                      placement="right"
                      isOpen={securedShipmentTooltipOpen}
                      target="securedShipment"
                      toggle={() => setSecuredShipmentTooltipOpen(!securedShipmentTooltipOpen)}
                    >
                      {t("Shipments declared as 'secured' can be onboarded without further security checks but require the shipper’s special status of a 'known consignor'. All other normal shipments need to run through security checks before boarding and are considered as 'unsecured'.")}
                    </Tooltip>
                  </FormGroup>
                </div>

                {/* Incoterm Section */}
                <h4 className='fw-bold mt-4'>{t("Incoterm")}</h4>
                <div className='e2g-shipping-handling gap-4 padding-15 radio-button-wrap'>
                  <FormGroup className="e2g-cargo-form-group">
                    <Label className='d-flex'>
                      <Field type="radio" name="role" value="importer" /> {t("I am an Importer")}
                    </Label>
                  </FormGroup>
                  <FormGroup className="e2g-cargo-form-group">
                    <Label className='d-flex'>
                      <Field type="radio" name="role" value="exporter" /> {t("I am an Exporter")}
                    </Label>
                  </FormGroup>
                  {errors.role && touched.role ? (
                    <div className="e2g-error-text text-danger">{errors.role}</div>
                  ) : null}
                </div>
                <div className='e2g-shipping-handling gap-2 row'>
                  <FormGroup className='col-6'>
                    <Label className='fw-bold' for="incoterm">{t("Incoterm")}</Label>
                    <Select
                      className='bg-light e2g-custom-height rounded-0 border-0'
                      id="incoterm"
                      options={[
                        { value: 'EXW', label: t('EXW') },
                        { value: 'FCA', label: t('FCA') },
                        { value: 'CPT', label: t('CPT') },
                        { value: 'CIP', label: t('CIP') },
                        { value: 'DAP', label: t('DAP') },
                        { value: 'DPU', label: t('DPU') },
                        { value: 'DDP', label: t('DDP') },
                      ]}
                      onChange={option => setFieldValue('incoterm', option?.label)}
                    />
                    {errors.incoterm && touched.incoterm ? (
                      <div className="e2g-error-text text-danger">{errors.incoterm}</div>
                    ) : null}
                  </FormGroup>
                  <FormGroup className='col-6'>
                    <Label for="incotermLocation">{t("Incoterm Location")}</Label>
                    <Field className='bg-light e2g-custom-height rounded-0 border-0' name="incotermLocation" type="text" as={Input} />
                    {errors.incotermLocation && touched.incotermLocation ? (
                      <div className="e2g-error-text text-danger">{errors.incotermLocation}</div>
                    ) : null}
                  </FormGroup>
                </div>
                <FieldArray name="references">
                  {({ push, remove }) => (
                    <>
                      {values.references.map((reference, index) => (
                        <Row key={index} className="mb-3 e2g-referrance">
                          <Col sm="5">
                            <FormGroup className='mt-3 d-flex mb-0 col-6 align-items-start gap-3 flex-column w-100'>
                              <Label className="e2g-cargo-form-label m-0 d-flex align-items-center" for={`references.${index}.referenceType`} >{t("Reference")}</Label>
                              <Select
                                className="bg-light e2g-custom-height rounded-0 border-0 e2g-custom-fonts form-control p-0 w-100"
                                id={`references.${index}.referenceType`}
                                options={referenceOptions}
                                onChange={option => setFieldValue(`references.${index}.referenceType`, option)}
                              />
                              {errors.references && errors.references[index] && errors.references[index].referenceType && touched.references && touched.references[index] && touched.references[index].referenceType ? (
                                <div className="e2g-error-text text-danger">{errors.references[index].referenceType}</div>
                              ) : null}
                            </FormGroup>
                          </Col>
                          <Col sm="5">
                            <FormGroup className='mt-3 d-flex mb-0 col-6 align-items-start gap-3 flex-column w-100'>
                              <Label className="e2g-cargo-form-label m-0 d-flex align-items-center" for={`references.${index}.referenceNo`}>{t("Reference No.")}</Label>
                              <Field
                                className="bg-light e2g-custom-height rounded-0 border-0 e2g-custom-fonts form-control  w-100"
                                name={`references.${index}.referenceNo`}
                                type="text"
                                as={Input}
                              />
                              {errors.references && errors.references[index] && errors.references[index].referenceNo && touched.references && touched.references[index] && touched.references[index].referenceNo ? (
                                <div className="e2g-error-text text-danger">{errors.references[index].referenceNo}</div>
                              ) : null}
                            </FormGroup>
                          </Col>
                          <Col sm="2" className="d-flex align-items-end mt-3 mb-3 ">
                            <Button color="primary" onClick={() => push(initialReference)}>
                              <FontAwesomeIcon icon={faPlus} />
                            </Button>
                            {values.references.length > 1 && (
                              <Button color="danger" onClick={() => remove(index)}>
                                <FontAwesomeIcon icon={faMinus} />
                              </Button>
                            )}
                          </Col>
                        </Row>
                      ))}
                    </>
                  )}
                </FieldArray>
                <Button type="submit" color="success" className="mt-0 p-3 mb-3" style={{ width: "100%", maxWidth: "300px", margin: "0" }}>{t("Submit")}</Button>
              </Form>
            )}
          </Formik>
        </div>
      </div>
    </div>
  );
};

export default Ship2Air;